import { useToast } from '@chakra-ui/react';
import {
  useCapturePropertyDetailsMutation,
  useGetPropertyDetailsLazyQuery,
} from '@client/graphql/__generated__/main-operations';
import {
  GetPropertyDetailsPayload,
  GetPropertyDetailsSuccessFragment,
  MlsListingSubType,
  MlsListingType,
  UsState,
} from '@client/graphql/__generated__/types';
import { GeocodingFeature } from '@mapbox/search-js-core';
import { FormikHelpers } from 'formik';
import isNil from 'lodash/isNil';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { getOffMarketListingPath } from '~/apps/consumer/utils/routes.utils';
import { AddOffMarketListingStep, OffMarketListingFormData } from './constants';

export const useAddOffMarketListingsActions = ({
  initialGeocode,
  onClose,
}: {
  initialGeocode?: GeocodingFeature | null;
  onClose: () => void;
}) => {
  const navigate = useNavigate();
  const toast = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [propertyDetails, setPropertyDetails] =
    useState<GetPropertyDetailsPayload | null>(null);
  const [selectedGeocode, setSelectedGeocode] =
    useState<GeocodingFeature | null>(initialGeocode || null);
  const [step, setStep] = useState<AddOffMarketListingStep>(
    AddOffMarketListingStep.ADDRESS
  );

  const [getPropertyDetails] = useGetPropertyDetailsLazyQuery();

  const [capturePropertyDetails] = useCapturePropertyDetailsMutation({
    refetchQueries: ['getUserCapturedPropertyDetails'],
  });

  const handleSubmit = async (values: OffMarketListingFormData) => {
    setIsLoading(true);
    try {
      const { data } = await capturePropertyDetails({
        variables: {
          input: {
            address: {
              streetAddress: values.streetAddress,
              city: values.city,
              state: values.state as UsState,
              zipCode: values.zipCode,
              unit: values.unit,
            },
            areaSqFt: !isNil(values.squareFootage)
              ? Number(values.squareFootage)
              : values.squareFootage,
            bathsFull: !isNil(values.fullBaths)
              ? Number(values.fullBaths)
              : values.fullBaths,
            bathsHalf: !isNil(values.halfBaths)
              ? Number(values.halfBaths)
              : values.halfBaths,
            bedrooms: !isNil(values.bedrooms)
              ? Number(values.bedrooms)
              : values.bedrooms,
            targetPrice: values.targetPrice,
            yearBuilt: !isNil(values.yearBuilt)
              ? Number(values.yearBuilt)
              : values.yearBuilt,
            propertyType: values.propertyType,
            propertySubtype: values.propertySubtype,
            subdivision: values.subdivision,
            schoolDistrict: values.schoolDistrict,
            coords: {
              lat:
                propertyDetails?.location?.coords?.lat ||
                selectedGeocode?.geometry?.coordinates[1] ||
                0,
              lon:
                propertyDetails?.location?.coords?.lng ||
                selectedGeocode?.geometry?.coordinates[0] ||
                0,
            },
          },
        },
      });
      setIsLoading(false);
      if (data?.capturePropertyDetails?.success) {
        navigate(
          getOffMarketListingPath(
            data?.capturePropertyDetails?.propertyDetails?.id || ''
          )
        );
        onClose();
      } else {
        toast({
          title: 'Error submitting form',
          status: 'error',
        });
      }
    } catch (error) {
      toast({
        title: 'Error submitting form',
        status: 'error',
      });
      setIsLoading(false);
    }
  };

  const handleAddressNext = async (
    values: Partial<OffMarketListingFormData>,
    setFieldValue: FormikHelpers<OffMarketListingFormData>['setFieldValue']
  ) => {
    setIsLoading(true);
    const { data } = await getPropertyDetails({
      variables: {
        input: {
          address: {
            streetAddress: values.streetAddress || '',
            city: values.city || '',
            state: values.state as UsState,
            zipCode: values.zipCode || '',
            unit: values.unit || '',
          },
        },
      },
    });
    if (
      data?.getPropertyDetails.result.__typename === 'GetPropertyDetailsFailed'
    ) {
      if (!selectedGeocode?.geometry?.coordinates?.length) {
        toast({
          title: 'Error finding property details',
          status: 'error',
        });
      } else {
        setStep(AddOffMarketListingStep.DETAILS);
      }
    } else {
      setPropertyDetails(data?.getPropertyDetails || null);

      const propertyDetailResults = data?.getPropertyDetails?.result as
        | GetPropertyDetailsSuccessFragment
        | undefined;

      propertyDetailResults?.areaSqFt &&
        (await setFieldValue(
          'squareFootage',
          propertyDetailResults.areaSqFt || 0
        ));
      propertyDetailResults?.yearBuilt &&
        (await setFieldValue(
          'yearBuilt',
          propertyDetailResults.yearBuilt || 0
        ));
      propertyDetailResults?.bedrooms &&
        (await setFieldValue('bedrooms', propertyDetailResults.bedrooms));
      propertyDetailResults?.bathsFull &&
        (await setFieldValue('fullBaths', propertyDetailResults.bathsFull));
      propertyDetailResults?.bathsHalf &&
        (await setFieldValue('halfBaths', propertyDetailResults.bathsHalf));
      await setFieldValue(
        'propertyType',
        propertyDetailResults?.propertyType || MlsListingType.RES
      );
      await setFieldValue(
        'propertySubtype',
        propertyDetailResults?.propertySubtype ||
          MlsListingSubType.SingleFamilyResidence
      );
      await setFieldValue(
        'subdivision',
        propertyDetailResults?.subdivision || ''
      );
      await setFieldValue(
        'schoolDistrict',
        propertyDetailResults?.schoolDistrict || ''
      );
      setStep(AddOffMarketListingStep.DETAILS);
    }
    setIsLoading(false);
  };

  return {
    handleSubmit,
    handleAddressNext,
    isLoading,
    step,
    setStep,
    propertyDetails,
    selectedGeocode,
    setSelectedGeocode,
  };
};
